import { Col, EmptyState, Form, FormInstance, Heading, Icon, Icons, Row, Textarea, useToast } from "@ir/ui";
import { useEffect, useState } from "react";
import { BackupRequestDataForSubmit, BackupRequestKeheFormValues } from "./BackupRequestInterface";
import { useDeductionDrawer } from "@/presentation/deduction/hooks";
import { GetBackupRequestUseCase } from "@/core/usecases/remittance/requestBackup/GetBackupRequest.useCase";
import { BackupRequestRepository } from "@/infrastructure/repositories/remittance/backupRequest/BackupRequest.repository";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { BackupRequestLookupsDTO } from "@/core/dto/deduction/remittance";
import { BackupRequestType } from "@/core/entities/deduction/remittance/BackupRequestLookups";
import { constructNote } from "./BackupRequestUtils";
import { createLogger } from "@/infrastructure/services/logger";

const client = GraphQlAdapter.getInstance().getClient();

interface BackupRequestKeheFormProps {
  backupRequestForm: FormInstance<BackupRequestKeheFormValues>;
  onBackupRequestSubmit?: (backupSubmitData: BackupRequestDataForSubmit) => void;
}

const BackupRequestKeheForm = ({ backupRequestForm, onBackupRequestSubmit }: BackupRequestKeheFormProps) => {
  const logger = createLogger("BackupRequestKeheForm");
  const { drawerInfo } = useDeductionDrawer();
  const { error: errorToast } = useToast();
  const [backupRequestTypes, setBackupRequestTypes] = useState<BackupRequestType[]>([]);
  const [isBackupRequestTypesLoading, setIsBackupRequestTypesLoading] = useState(false);
  const [noteTemplate, setNoteTemplate] = useState<string>("");

  useEffect(() => {
    const fetchBackupRequestConfig = async () => {
      try {
        setIsBackupRequestTypesLoading(true);
        const repository = new BackupRequestRepository(client);
        const useCase = new GetBackupRequestUseCase(repository);
        const response = await useCase.execute(
          new BackupRequestLookupsDTO({
            purchaserKey: drawerInfo?.purchaserKey ?? "",
          })
        );
        setBackupRequestTypes(response.disputeTypes.map(type => new BackupRequestType(type)) ?? []);

        const note = constructNote(response.noteTemplate ?? "", [drawerInfo?.invoiceNumber ?? ""]);
        setNoteTemplate(note);

        // Check if the 'note' field is already populated by the user before setting it
        const currentNoteValue = backupRequestForm.getFieldValue("note");
        if (!currentNoteValue || currentNoteValue === noteTemplate) {
          backupRequestForm.setFieldsValue({
            note: note,
          });
        }
      } catch (error) {
        logger.error("Error fetching request backup types", error);
        errorToast({
          content: "Failed to fetch request backup types",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setIsBackupRequestTypesLoading(false);
      }
    };

    fetchBackupRequestConfig();
  }, [drawerInfo?.purchaserKey, errorToast, backupRequestForm, noteTemplate, drawerInfo?.invoiceNumber]);

  useEffect(() => {
    if (!backupRequestTypes.length) return;
    const existingValue = backupRequestForm.getFieldValue("disputeTypeKey");
    if (existingValue?.value) return;

    const [firstType] = backupRequestTypes;
    if (!firstType) return;
    backupRequestForm.setFieldValue("disputeTypeKey", {
      label: firstType.disputeTypeName,
      value: firstType.disputeTypeId,
    });
  }, [backupRequestTypes, backupRequestForm]);

  const handleSubmit = (values: BackupRequestKeheFormValues) => {
    const backupSubmitData: BackupRequestDataForSubmit = {
      remittanceItemId: drawerInfo?.remittanceItemId ?? 0,
      claimNumber: drawerInfo?.invoiceNumber ?? "",
      disputeTypeId: Number(backupRequestTypes?.at(0)?.disputeTypeId),
      note: values.note,
    };
    onBackupRequestSubmit?.(backupSubmitData);
  };

  if (!backupRequestForm) {
    return (
      <EmptyState
        className="bg-white border-1 border-color-grey-25 px-4 py-5 m-2"
        icon={Icons.Settings}
        title={
          <Heading level={6} className="text-grey-700 font-bold mt-3">
            Loading...
          </Heading>
        }
      />
    );
  }

  return (
    <Form form={backupRequestForm} onFinish={handleSubmit} className="mb-0">
      <Row>
        <Col span={12}>
          <Form.Item
            name="note"
            rules={[
              { required: true, message: "Notes are required." },
              { max: 500, message: "Notes must be less than 500 characters." },
              { whitespace: true, message: "Notes cannot contain leading or trailing spaces." },
            ]}
            label="Notes"
            className="mt-3"
          >
            <Textarea minRows={3} showCharCount maxLength={500} placeholder={isBackupRequestTypesLoading ? "Notes are loading..." : "Enter notes..."} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default BackupRequestKeheForm;
